import { format } from "date-fns";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { ClientSalesData, InvoiceDetail } from "~/hooks/useClientSalesDashboard";
import { formatCurrency } from "~/utils/helpers";

/**
 * Exports client sales data to an Excel file with two sheets:
 * 1. Summary by client (ranked by sales)
 * 2. Invoice details
 */
export const exportClientSalesToExcel = async (
  clientsData: ClientSalesData[],
  invoiceDetails: InvoiceDetail[],
  startDate?: Date,
  endDate?: Date
): Promise<void> => {
  // Create workbook
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Summary by client
  const summaryData = clientsData.map((client, index) => ({
    "#": index + 1,
    Cliente: client.clientName,
    RUT: client.clientTaxId,
    "Ventas Totales (Neto)": client.totalSales,
    "# Facturas": client.invoiceCount,
    "% del Total": `${client.percentage.toFixed(2)}%`,
  }));

  const summarySheet = XLSX.utils.json_to_sheet(summaryData);

  // Set column widths for summary sheet
  summarySheet["!cols"] = [
    { wch: 5 }, // #
    { wch: 40 }, // Cliente
    { wch: 15 }, // RUT
    { wch: 20 }, // Ventas Totales
    { wch: 12 }, // # Facturas
    { wch: 12 }, // % del Total
  ];

  XLSX.utils.book_append_sheet(workbook, summarySheet, "Resumen por Cliente");

  // Sheet 2: Invoice details
  const detailsData = invoiceDetails.map((invoice) => ({
    "# Factura": invoice.bsaleNumber,
    Cliente: invoice.clientName,
    RUT: invoice.clientTaxId,
    "Fecha Emisión": invoice.emissionDate
      ? dayjs.unix(invoice.emissionDate).format("DD/MM/YYYY")
      : "N/A",
    "Monto Neto": invoice.netAmount,
    IVA: invoice.taxAmount,
    "Monto Total": invoice.totalAmount,
  }));

  const detailsSheet = XLSX.utils.json_to_sheet(detailsData);

  // Set column widths for details sheet
  detailsSheet["!cols"] = [
    { wch: 12 }, // # Factura
    { wch: 40 }, // Cliente
    { wch: 15 }, // RUT
    { wch: 15 }, // Fecha Emisión
    { wch: 15 }, // Monto Neto
    { wch: 12 }, // IVA
    { wch: 15 }, // Monto Total
  ];

  XLSX.utils.book_append_sheet(workbook, detailsSheet, "Detalle de Facturas");

  // Generate filename with date range
  const startStr = startDate ? format(startDate, "yyyyMMdd") : "inicio";
  const endStr = endDate ? format(endDate, "yyyyMMdd") : "fin";
  const fileName = `ventas_por_cliente_${startStr}_${endStr}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, fileName);
};
